import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import http, { createFetchAPI } from '../http';
import { RcFile, UploadFile } from 'fave-ui';
import { TableSettingsType } from '../../types/configTypes';
import { ValidityType } from '../../types/dataTypes';

export const queryKeys = {
  FavePassOutletSummary: 'FavePassOutletSummary',
  AvailableFavePassOutlets: 'AvailableFavePassOutlets',
  FetchLinkedOutlets: 'FetchLinkedOutlets',
  fetchFavePassDetails: 'fetchFavePassDetails',
  FetchBankAccounts: 'FetchBankAccounts',
};

// to create fave pass
const createFavePass = (payload: CreateFavePassPayload) =>
  http.post<CreateFavePassPayload>('/v1/fave_passes', payload.fave_pass);

export const useCreateFavePass = () => useMutation(createFavePass);

// to update fave pass
const updateFavePass = ({ fave_pass, fave_pass_id }: UpdateFavePassPayload) =>
  http.put<UpdateFavePassPayload>(`/v1/fave_passes/${fave_pass_id}`, fave_pass);

export const useUpdateFavePass = () => {
  const queryClient = useQueryClient();

  return useMutation(updateFavePass, {
    onSuccess: () =>
      queryClient.invalidateQueries(queryKeys.fetchFavePassDetails),
  });
};

const fetchFavePassDetails = createFetchAPI<
  FetchFavePassDetailsPayload,
  FetchFavePassDetailsResponse
>();

// to fetch fave pass to prefill the form to update fave pass
export const useFetchFavePassDetails = (
  props: FetchFavePassDetailsPayload,
  enabled: boolean,
) =>
  useQuery<FetchFavePassDetailsResponse>(
    [queryKeys.fetchFavePassDetails, { url: `/v1/fave_passes/${props.id}` }],
    fetchFavePassDetails,
    { enabled, staleTime: 0 },
  );

// to fetch enums related to fave pass
const fetchEnums = createFetchAPI<{}, FetchFavePassEnumsResponse>();

export const useFetchEnums = () =>
  useQuery<FetchFavePassEnumsResponse>(
    ['fetchEnums', { url: `/v1/enums/FavePass` }],
    fetchEnums,
  );

// to fetch main categories for main category dropdown
const fetchMainCategories = createFetchAPI<{}, FetchMainCategoriesResponse>();

export const useFetchMainCategories = () =>
  useQuery<FetchMainCategoriesResponse>(
    ['fetchMainCategories', { url: `/v1/main_categories` }],
    fetchMainCategories,
  );

const fetchOutletSummary = createFetchAPI<{}, FetchOutletSummaryResponse>();
export const useFavePassOutletSummary = ({
  favePassId,
}: {
  favePassId: number;
}) =>
  useQuery(
    [
      queryKeys.FavePassOutletSummary,
      { url: `/v1/fave_passes/${favePassId}/fave_pass_outlets/summary` },
    ],
    fetchOutletSummary,
  );

const fetchAvailableOutlets = createFetchAPI<{}, AvailableOutletsResponse>();

export const useFetchAvailableFavePassOutlets = ({
  favePassId,
  filter,
  page,
  pageSize,
  enabled,
}: {
  favePassId: number;
  filter: string;
  page: number;
  pageSize: number;
  enabled: boolean;
}) =>
  useQuery(
    [
      queryKeys.AvailableFavePassOutlets,
      {
        url: `/v1/fave_passes/${favePassId}/available_outlets`,
        page,
        filter,
        limit: pageSize,
      },
    ],
    fetchAvailableOutlets,
    { enabled },
  );

const mutateFavePassOutletLink = ({
  favePassId,
  ...restPayload
}: {
  favePassId: number;
  all_outlets: boolean;
  outlet_ids: number[];
}) => http.post(`/v1/fave_passes/${favePassId}/fave_pass_outlets`, restPayload);

export const useMutateFavePassOutletLink = () => {
  const queryClient = useQueryClient();

  return useMutation(mutateFavePassOutletLink, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.AvailableFavePassOutlets);
      queryClient.invalidateQueries(queryKeys.FavePassOutletSummary);
      queryClient.invalidateQueries(queryKeys.FetchLinkedOutlets);
    },
  });
};

const fetchFavePassLinkedOutletsList = createFetchAPI<
  {},
  FavePassOutletsResponseType
>();

export const useFetchFavePassOutletsList = (
  { favePassId, ...props }: FavePassOutletsPayload,
  { enabled }: UseQueryOptions,
) =>
  useQuery(
    [
      queryKeys.FetchLinkedOutlets,
      { url: `/v1/fave_passes/${favePassId}/fave_pass_outlets`, ...props },
    ],
    fetchFavePassLinkedOutletsList,
    { enabled },
  );

const mutateUnlinkOutlet = ({
  favePassOutletId,
  favePassId,
}: {
  favePassOutletId: number;
  favePassId: number;
}) =>
  http.delete(
    `/v1/fave_passes/${favePassId}/fave_pass_outlets/${favePassOutletId}`,
  );

export const useMutateUnlinkFavePassOutlet = () => {
  const queryClient = useQueryClient();

  return useMutation(mutateUnlinkOutlet, {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.FetchLinkedOutlets);
    },
  });
};

const fetchBankAccounts = createFetchAPI<{}, FinanceAccountsResponse>();

export const useFetchBankAccounts = ({ companyId }: { companyId: number }) =>
  useQuery(
    [
      queryKeys.FetchBankAccounts,
      {
        url: `/v1/companies/${companyId}/finance_accounts`,
      },
    ],
    fetchBankAccounts,
  );

export type CreateFavePassForm = {
  name: string;
  description: string;
  image_url: UploadFile[];
  total_sessions: number;
  original_amount: number;
  discounted_amount: number;
  start_date: string;
  end_date: string;
  status: string;
  max_cap: number;
  user_purchase_limit: number;
  validity: number;
  validity_type: ValidityType;
  what_you_get: string;
  fine_print: string;
  cancellation_policy: string;
  main_category_id: number;
  fee_percentage: number;
  grabpay_later_fee_percentage: number;
  reservation_requirement_type: string;
};

export type FavePass = Omit<CreateFavePassForm, 'image_url'> & {
  company_id: string;
  image: RcFile;
};

export type CreateFavePassPayload = {
  fave_pass: FormData;
};

export type UpdateFavePassPayload = {
  fave_pass: FormData;
  fave_pass_id: string;
};

// id from fave pass listing table
type FetchFavePassDetailsPayload = { id: string };

type FetchFavePassDetailsResponse = {
  id: string;
  name: string;
  description: string;
  image_url: string;
  total_sessions: number;
  original_amount: {
    currency: string;
    amount: string;
  };
  discounted_amount: {
    currency: string;
    amount: number;
  };
  start_date: string;
  end_date: string;
  status: 'active';
  max_cap: number;
  user_purchase_limit: number;
  validity: number;
  what_you_get: string;
  fine_print: string;
  cancellation_policy: string;
  main_category_id: number;
  fee_percentage: number;
  reservation_requirement_type: 'not_required';
  grabpay_later_fee_percentage: number;
  finance_account_id: number;
};

export type FetchFavePassEnumsResponse = {
  status: { key: string; label: string }[];
  reservation_requirement_type: { key: string; label: string }[];
};

type MainCategories = {
  id: string;
  category_type: string; // FE display this in dropdown
  status: string;
  order: number;
};

export type FetchMainCategoriesResponse = {
  main_categories: MainCategories[];
};

type FetchOutletSummaryResponse = {
  linked_outlets: number;
  active_linked_outlets: number;
  inactive_linked_outlets: number;
};

export type FavePassOutlet = {
  id: number;
  name: string;
  status: 'active' | 'inactive';
};

type AvailableOutletsResponse = {
  outlets: FavePassOutlet[];
  count: number;
};

type FavePassOutletsPayload = {
  favePassId: number;
} & TableSettingsType;

type FavePassOutletsResponseType = {
  fave_pass_outlets: {
    id: number;
    outlet: FavePassOutlet;
  }[];
  count: number;
};

type FinanceAccountsResponse = {
  finance_accounts: FinanceAccount[];
  count: number;
};

type FinanceAccount = {
  id: string;
  bank_account_name: string;
  bank_account_number: string;
  outlets: FinanceAccountOutlet[];
  bank_info: BankInfo;
};

type FinanceAccountOutlet = {
  id: string;
  name: string;
};

type BankInfo = {
  id: string;
  name: string;
};
